/**
 * PassportNumberR — passport document number field.
 * Handles type: passport_number.
 * Uppercases alphanumeric input; max 30 chars.
 */

import { useFormContext } from 'react-hook-form';
import { TextField } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { buildValidationRules } from '../validation/fromSchema';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function PassportNumberR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  if (pdfMode) {
    const value = getValues(name);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5 uppercase font-mono tracking-wider">
          {value ?? <span className="text-slate-400 italic normal-case font-sans tracking-normal">Not provided</span>}
        </p>
      </div>
    );
  }

  return (
    <TextField
      name={name}
      label={label}
      hint={resolveHelpText(field, audience) ?? 'Uppercase letters and numbers only, as shown in your passport'}
      required={field.required}
      type="text"
      placeholder={field.example ?? 'PA1234567'}
      tooltip={lookupFieldTooltip(field.canonical_name)}
      transformOnBlur={(value) => value.toUpperCase()}
      rules={buildValidationRules(field)}
    />
  );
}
